import { useState, useRef } from 'react';
import {
  Modal,
  Stack,
  Button,
  Group,
  Text,
  Radio,
  Checkbox,
  Paper,
  Alert,
} from '@mantine/core';
import { IconPrinter, IconAlertCircle } from '@tabler/icons-react';
import { QRCodeSVG } from 'qrcode.react';
import type { Asset } from '@/types/models';

interface BulkPrintQRModalProps {
  opened: boolean;
  onClose: () => void;
  assets: Asset[];
}

type QRSize = 'small' | 'medium' | 'large';

const QR_SIZE_CONFIG = {
  small: {
    label: 'Small (2" x 2")',
    qrSize: 120,
    width: '2in',
    height: '2in',
    fontSize: '8pt',
    tagFontSize: '10pt',
    perRow: 4,
  },
  medium: {
    label: 'Medium (3" x 3")',
    qrSize: 180,
    width: '3in',
    height: '3in',
    fontSize: '10pt',
    tagFontSize: '12pt',
    perRow: 2,
  },
  large: {
    label: 'Large (4" x 4")',
    qrSize: 240,
    width: '4in',
    height: '4in',
    fontSize: '12pt',
    tagFontSize: '14pt',
    perRow: 2,
  },
};

export function BulkPrintQRModal({ opened, onClose, assets }: BulkPrintQRModalProps) {
  const [qrSize, setQRSize] = useState<QRSize>('medium');
  const [showCutLines, setShowCutLines] = useState(true);
  const [showAssetInfo, setShowAssetInfo] = useState(true);
  const qrContainerRef = useRef<HTMLDivElement>(null);

  const handlePrint = () => {
    const config = QR_SIZE_CONFIG[qrSize];
    const printWindow = window.open('', '_blank');
    if (!printWindow) {
      alert('Please allow popups to print QR codes');
      return;
    }

    // Get all QR code SVGs from the hidden container
    const qrElements = qrContainerRef.current?.querySelectorAll('svg');
    if (!qrElements || qrElements.length === 0) {
      alert('Failed to generate QR codes');
      return;
    }

    // Generate HTML for each QR code
    const qrCodeHtml = assets
      .map((asset, index) => {
        const svg = qrElements[index];
        const svgData = svg ? new XMLSerializer().serializeToString(svg) : '';

        return `
          <div class="qr-item">
            <div class="qr-content">
              <div class="qr-code">${svgData}</div>
              ${
                showAssetInfo
                  ? `
                <div class="asset-info">
                  <div class="asset-tag">${asset.tag_id}</div>
                  <div class="asset-description">${asset.description || ''}</div>
                </div>
              `
                  : ''
              }
            </div>
          </div>
        `;
      })
      .join('');

    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>Asset QR Codes - Bulk Print</title>
          <style>
            * {
              margin: 0;
              padding: 0;
              box-sizing: border-box;
            }

            @page {
              margin: 0.5in;
              size: letter portrait;
            }

            body {
              font-family: Arial, sans-serif;
              -webkit-print-color-adjust: exact;
              print-color-adjust: exact;
            }

            .qr-grid {
              display: grid;
              grid-template-columns: repeat(${config.perRow}, 1fr);
              gap: 0;
              width: 100%;
            }

            .qr-item {
              width: ${config.width};
              height: ${config.height};
              display: flex;
              align-items: center;
              justify-content: center;
              page-break-inside: avoid;
              ${
                showCutLines
                  ? `
                border: 1px dashed #ccc;
                border-right: none;
                border-bottom: none;
              `
                  : ''
              }
            }

            ${
              showCutLines
                ? `
              .qr-item:nth-child(${config.perRow}n) {
                border-right: 1px dashed #ccc;
              }

              .qr-grid .qr-item:nth-last-child(-n+${config.perRow}) {
                border-bottom: 1px dashed #ccc;
              }
            `
                : ''
            }

            .qr-content {
              text-align: center;
              padding: 8px;
            }

            .qr-code {
              display: flex;
              justify-content: center;
              margin-bottom: ${showAssetInfo ? '8px' : '0'};
            }

            .qr-code svg {
              width: ${config.qrSize}px !important;
              height: ${config.qrSize}px !important;
              display: block;
            }

            .asset-info {
              text-align: center;
            }

            .asset-tag {
              font-size: ${config.tagFontSize};
              font-weight: bold;
              margin-bottom: 2px;
              word-wrap: break-word;
            }

            .asset-description {
              font-size: ${config.fontSize};
              color: #666;
              word-wrap: break-word;
              overflow: hidden;
              text-overflow: ellipsis;
              display: -webkit-box;
              -webkit-line-clamp: 2;
              -webkit-box-orient: vertical;
            }

            @media print {
              body {
                margin: 0;
              }
            }
          </style>
        </head>
        <body>
          <div class="qr-grid">
            ${qrCodeHtml}
          </div>
        </body>
      </html>
    `);

    printWindow.document.close();
    setTimeout(() => {
      printWindow.print();
    }, 500);

    onClose();
  };

  return (
    <>
      <Modal opened={opened} onClose={onClose} title="Bulk Print QR Codes" size="md">
        <Stack gap="md">
          {assets.length === 0 ? (
            <Alert icon={<IconAlertCircle size={16} />} color="yellow">
              No assets selected for printing
            </Alert>
          ) : (
            <>
              <Paper p="sm" withBorder>
                <Text size="sm" fw={500}>
                  Selected Assets: {assets.length}
                </Text>
              </Paper>

              <Stack gap="xs">
                <Text size="sm" fw={500}>
                  QR Code Size
                </Text>
                <Radio.Group value={qrSize} onChange={(val) => setQRSize(val as QRSize)}>
                  <Stack gap="xs">
                    {(Object.keys(QR_SIZE_CONFIG) as QRSize[]).map((size) => (
                      <Radio
                        key={size}
                        value={size}
                        label={
                          <div>
                            <Text size="sm">{QR_SIZE_CONFIG[size].label}</Text>
                            <Text size="xs" c="dimmed">
                              {QR_SIZE_CONFIG[size].perRow} per row
                            </Text>
                          </div>
                        }
                      />
                    ))}
                  </Stack>
                </Radio.Group>
              </Stack>

              <Stack gap="xs">
                <Text size="sm" fw={500}>
                  Print Options
                </Text>
                <Checkbox
                  label="Show cut lines (dashed borders)"
                  checked={showCutLines}
                  onChange={(e) => setShowCutLines(e.currentTarget.checked)}
                />
                <Checkbox
                  label="Show asset tag and description"
                  checked={showAssetInfo}
                  onChange={(e) => setShowAssetInfo(e.currentTarget.checked)}
                />
              </Stack>

              <Group justify="flex-end" mt="md">
                <Button variant="subtle" onClick={onClose}>
                  Cancel
                </Button>
                <Button leftSection={<IconPrinter size={16} />} onClick={handlePrint}>
                  Print QR Codes
                </Button>
              </Group>
            </>
          )}
        </Stack>
      </Modal>

      {/* Hidden container to render QR codes for printing */}
      <div ref={qrContainerRef} style={{ display: 'none' }}>
        {assets.map((asset) => {
          const qrValue = JSON.stringify({
            type: 'asset',
            id: asset.id,
            tag_id: asset.tag_id,
          });
          return (
            <QRCodeSVG
              key={asset.id}
              value={qrValue}
              size={QR_SIZE_CONFIG[qrSize].qrSize}
              level="H"
              includeMargin
            />
          );
        })}
      </div>
    </>
  );
}
